import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Modal, message } from 'antd';
import moment from 'moment';
import { modalBodyStyle } from './constants';
import { CalendarBlock, TimeRangeBlock } from './components';
import { resetSelectedRequest } from '../../actions/acceptanceRequestToCalendarActions';
import useRequestInvitationDays from '../../queries/useRequestInvitationDays';
import { selectedRequestSelector } from '../../reducers/acceptanceRequestToCalendarReducer';
import useAddToAcceptanceCalendar from '../../mutations/useAddToAcceptanceCalendar';
import getRequest from '../../requests/getRequest';

const AcceptanceRequestToCalendar = ({ afterSubmit, onCancel }) => {
  const dispatch = useDispatch();
  const request = useSelector(selectedRequestSelector);

  const { data: invitationDays } = useRequestInvitationDays(request?.id, {
    enabled: Boolean(request),
  });

  const [date, setDate] = useState(undefined);
  const [timeRange, setTimeRange] = useState(undefined);
  const [hasRequestData, setHasRequestData] = useState(false);

  const hideModal = () => dispatch(resetSelectedRequest());

  const handleCancel = () => {
    hideModal();

    if (typeof onCancel === 'function') {
      onCancel({ hasRequestData });
    }
  };

  const handleCalendarChange = (date) => {
    setDate(date);

    if (timeRange) {
      setTimeRange(undefined);
    }
  };

  const handleTimeRangeChange = (range) => {
    setTimeRange(range);
  };

  const handleResetState = () => {
    setTimeRange(undefined);
    setDate(undefined);
  };

  const handleAddToCalendarSettled = async (data, _error, variables) => {
    if (data !== 'Error') {
      message.success(
        request.taskId
          ? 'Изменения сохранены'
          : 'Заявка добавлена в календарь приемки'
      );

      const updatedRequest = await getRequest(
        variables.values.requestId,
        'Не удалось загрузить обновленную заявку. Попробуйте обновить страницу'
      );

      if (typeof afterSubmit === 'function') {
        afterSubmit({
          updatedRequest:
            updatedRequest?.length === 1 ? updatedRequest[0] : null,
          hasRequestData,
        });
      }

      hideModal();
    } else {
      hideModal();
    }
  };

  const { mutate: addToAcceptanceCalendar, isLoading: isAddLoading } =
    useAddToAcceptanceCalendar({
      onSettled: handleAddToCalendarSettled,
    });

  const handleOk = () => {
    const values = {
      requestId: request.id,
      date: date.clone().utc().format(),
      intervalId: timeRange.id,
    };

    addToAcceptanceCalendar({ values });
  };

  useEffect(() => {
    let date;

    if (invitationDays?.length) {
      if (request?.taskId) {
        const taskStartStr = moment(request.taskStart).format('YYYY-MM-DD');
        const index = invitationDays.indexOf(taskStartStr);
        date = moment(invitationDays[index === -1 ? 0 : index]);
      } else {
        date = moment(invitationDays[0]);
      }
    }

    setDate(date);
  }, [invitationDays]);

  useEffect(() => {
    setHasRequestData(request && Object.keys(request).length > 1);
  }, [request]);

  return (
    <Modal
      visible={Boolean(request)}
      title="Добавить заявку о приемке в календарь"
      bodyStyle={modalBodyStyle}
      destroyOnClose
      onCancel={handleCancel}
      afterClose={handleResetState}
      okText={request?.taskId ? 'Изменить' : 'Добавить'}
      okButtonProps={{ disabled: !timeRange || !date || isAddLoading }}
      onOk={handleOk}
      confirmLoading={isAddLoading}
    >
      <CalendarBlock selectedDate={date} onChange={handleCalendarChange} />
      {date && (
        <TimeRangeBlock
          selectedDate={date}
          timeRange={timeRange}
          onChange={handleTimeRangeChange}
        />
      )}
    </Modal>
  );
};

export default AcceptanceRequestToCalendar;
